/**
 * Composants partagés pour le rendu des fiches de personnage.
 * Utilisés par CharacterSheetPage (joueur) et GMCharacterSheetPage (MJ).
 */
import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronUp,
  Filter,
  RotateCcw,
  History,
  Loader2,
} from 'lucide-react';
import { t } from '../../lib/i18n';

const API_URL = '/api';

// ── HistoryChangeLine ─────────────────────────────────────────────────────
export function HistoryChangeLine({ change, language }) {
  const label = change.label || change.path;

  const formatVal = (v) => {
    if (v === null || v === undefined) return `(${t('history_empty', language)})`;
    if (typeof v === 'boolean') return v ? '✓' : '✗';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const isAdded   = change.old === null && change.new !== null;
  const isRemoved = change.new === null && change.old !== null;

  return (
    <li className="text-xs flex flex-wrap items-baseline gap-1 text-muted-foreground">
      <span className="font-medium text-foreground/80 font-mono">{label}</span>
      {isAdded ? (
        <span className="text-green-600 dark:text-green-400">
          + {formatVal(change.new)}
        </span>
      ) : isRemoved ? (
        <span className="text-red-500 dark:text-red-400">
          − {formatVal(change.old)}
        </span>
      ) : (
        <>
          <span className="line-through opacity-50">{formatVal(change.old)}</span>
          <span className="text-muted-foreground">→</span>
          <span className="text-primary font-medium">{formatVal(change.new)}</span>
        </>
      )}
    </li>
  );
}

// ── HistoryEntry ──────────────────────────────────────────────────────────
export function HistoryEntry({ entry, isMJ, language }) {
  const [expanded, setExpanded] = useState(false);

  const allChanges     = entry.changes_json?.paths || [];
  const visibleChanges = isMJ
    ? allChanges
    : allChanges.filter(c => c.visibility !== 'gm_only');
  const hiddenCount    = allChanges.length - visibleChanges.length;

  const isCreate = entry.action === 'create';
  const date     = new Date(entry.created_at);
  const locale   = language === 'fr' ? 'fr-FR' : 'en-US';

  const roleLabel =
    entry.modified_role === 'gm'    ? t('history_role_gm', language)    :
    entry.modified_role === 'admin' ? t('history_role_admin', language)  :
                                      t('history_role_player', language);

  return (
    <div className="relative pl-6 border-l-2 border-border/50 pb-5 last:pb-0">
      <div className={`absolute -left-[5px] top-[14px] h-2.5 w-2.5 rounded-full ring-2 ring-background ${
        isCreate ? 'bg-green-500' : 'bg-primary'
      }`} />
      <div className="glass-card rounded-lg p-3 space-y-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">
              {isCreate
                ? t('history_action_create', language)
                : t('history_action_update', language)}
            </span>
            <Badge variant="outline"   className="text-xs py-0 h-5">{roleLabel}</Badge>
            <Badge variant="secondary" className="text-xs py-0 h-5 font-mono">
              v{entry.sheet_version}
            </Badge>
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })}
            {' · '}
            {date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        {visibleChanges.length > 0 && (
          <div>
            <button
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              onClick={() => setExpanded(e => !e)}
            >
              {visibleChanges.length === 1
                ? `1 ${t('history_changed_fields', language)}`
                : `${visibleChanges.length} ${t('history_changed_fields_plural', language)}`}
              {expanded
                ? <ChevronUp   className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />}
            </button>
            {expanded && (
              <ul className="mt-2 space-y-1.5 pl-2 border-l border-border/40">
                {visibleChanges.map((change, i) => (
                  <HistoryChangeLine key={i} change={change} language={language} />
                ))}
              </ul>
            )}
          </div>
        )}
        {hiddenCount > 0 && (
          <p className="text-xs text-muted-foreground/70 italic">
            {t('history_gm_only_hidden', language)}
          </p>
        )}
      </div>
    </div>
  );
}

// ── HistoryTab ────────────────────────────────────────────────────────────
export function HistoryTab({ campaignId, sheetId, isMJ, language }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor,  setCursor]  = useState(null);
  const [filters, setFilters] = useState({ from: '', to: '', path: '' });
  const [draft,   setDraft]   = useState({ from: '', to: '', path: '' });

  const loadHistory = useCallback(async (nextCursor, appliedFilters, reset) => {
    if (!sheetId) return;
    setLoading(true);
    try {
      const params = { limit: 20 };
      if (nextCursor)             params.cursor    = nextCursor;
      if (appliedFilters.from)    params.from_date = appliedFilters.from;
      if (appliedFilters.to)      params.to_date   = appliedFilters.to;
      if (appliedFilters.path)    params.path      = appliedFilters.path;

      const res = await axios.get(
        `${API_URL}/campaigns/${campaignId}/character-sheets/${sheetId}/history`,
        { params }
      );
      if (reset) setItems(res.data.items || []);
      else       setItems(prev => [...prev, ...(res.data.items || [])]);
      setCursor(res.data.next_cursor || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setLoading(false);
    }
  }, [sheetId, campaignId, language]);

  useEffect(() => {
    const empty = { from: '', to: '', path: '' };
    setItems([]);
    setCursor(null);
    setFilters(empty);
    setDraft(empty);
    loadHistory(null, empty, true);
  }, [sheetId, loadHistory]);

  const applyFilters = () => {
    const f = { ...draft };
    setFilters(f);
    setItems([]);
    setCursor(null);
    loadHistory(null, f, true);
  };

  const resetFilters = () => {
    const empty = { from: '', to: '', path: '' };
    setDraft(empty);
    setFilters(empty);
    setItems([]);
    setCursor(null);
    loadHistory(null, empty, true);
  };

  const hasActiveFilters = filters.from || filters.to || filters.path;

  return (
    <div className="space-y-4">
      <Card className="glass-card">
        <div className="p-3 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Filter className="h-3.5 w-3.5" />
            {language === 'fr' ? 'Filtres' : 'Filters'}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t('history_filter_from', language)}</Label>
              <Input
                type="date"
                className="h-8 text-xs"
                value={draft.from}
                onChange={e => setDraft(d => ({ ...d, from: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('history_filter_to', language)}</Label>
              <Input
                type="date"
                className="h-8 text-xs"
                value={draft.to}
                onChange={e => setDraft(d => ({ ...d, to: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('history_filter_path', language)}</Label>
              <Input
                className="h-8 text-xs"
                placeholder={language === 'fr' ? 'ex: level' : 'e.g. level'}
                value={draft.path}
                onChange={e => setDraft(d => ({ ...d, path: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && applyFilters()}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={applyFilters} disabled={loading}>
              <Filter className="h-3.5 w-3.5 mr-1" />
              {t('history_filter_apply', language)}
            </Button>
            {hasActiveFilters && (
              <Button size="sm" variant="ghost" onClick={resetFilters} disabled={loading}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                {t('history_filter_reset', language)}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-12 gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t('history_loading', language)}</span>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 space-y-3">
          <History className="h-10 w-10 mx-auto text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">{t('history_no_entries', language)}</p>
        </div>
      ) : (
        <div className="ml-1 space-y-0">
          {items.map(entry => (
            <HistoryEntry
              key={entry.id}
              entry={entry}
              isMJ={isMJ}
              language={language}
            />
          ))}
        </div>
      )}

      {cursor && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadHistory(cursor, filters, false)}
            disabled={loading}
          >
            {loading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {t('history_load_more', language)}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── FieldRenderer ─────────────────────────────────────────────────────────
export function FieldRenderer({ name, schema, value, onChange, readOnly }) {
  const type       = schema?.type;
  const title      = schema?.title || name;
  const enumValues = schema?.enum;

  if (!schema) return null;

  if (enumValues) {
    return (
      <div className="space-y-1">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</Label>
        {readOnly ? (
          <p className="text-sm font-medium">{value ?? '—'}</p>
        ) : (
          <select
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={value ?? ''}
            onChange={e => onChange(name, e.target.value)}
          >
            <option value="">—</option>
            {enumValues.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
      </div>
    );
  }

  if (type === 'boolean') {
    return (
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</Label>
        {readOnly ? (
          <Badge variant={value ? 'default' : 'outline'}>{value ? '✓' : '✗'}</Badge>
        ) : (
          <Switch
            checked={!!value}
            onCheckedChange={checked => onChange(name, checked)}
          />
        )}
      </div>
    );
  }

  if (type === 'integer') {
    return (
      <div className="space-y-1">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</Label>
        {readOnly ? (
          <p className="text-sm font-bold">{value ?? '—'}</p>
        ) : (
          <Input
            type="number"
            min={schema.minimum}
            max={schema.maximum}
            value={value ?? ''}
            onChange={e => onChange(name, e.target.value === '' ? '' : parseInt(e.target.value, 10))}
            className="h-8 text-sm"
          />
        )}
      </div>
    );
  }

  const isLong = ['notes', 'backstory', 'traits', 'background', 'equipment',
    'features', 'abilities', 'bonds', 'ideals', 'flaws', 'personality',
    'allies', 'history'].some(kw => name.toLowerCase().includes(kw));

  if (type === 'string') {
    return (
      <div className="space-y-1">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</Label>
        {readOnly ? (
          <p className="text-sm whitespace-pre-wrap">{value || '—'}</p>
        ) : isLong ? (
          <Textarea
            value={value ?? ''}
            onChange={e => onChange(name, e.target.value)}
            rows={3}
            className="text-sm resize-none"
          />
        ) : (
          <Input
            value={value ?? ''}
            onChange={e => onChange(name, e.target.value)}
            className="h-8 text-sm"
            maxLength={schema.maxLength}
          />
        )}
      </div>
    );
  }

  if (type === 'object' && schema.properties) {
    const subProps = schema.properties;
    return (
      <div className="space-y-2 pl-2 border-l-2 border-border/50">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(subProps).map(([subName, subSchema]) => (
            <FieldRenderer
              key={subName}
              name={subName}
              schema={subSchema}
              value={value?.[subName]}
              onChange={(_, v) => {
                const updated = { ...(value || {}), [subName]: v };
                onChange(name, updated);
              }}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>
    );
  }

  if (type === 'array') {
    const display = Array.isArray(value) ? value.join(', ') : (value ?? '');
    return (
      <div className="space-y-1">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</Label>
        {readOnly ? (
          <p className="text-sm">{display || '—'}</p>
        ) : (
          <Input
            placeholder={`${title} (séparés par des virgules)`}
            value={display}
            onChange={e => onChange(name, e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            className="h-8 text-sm"
          />
        )}
      </div>
    );
  }

  return null;
}

// ── SheetSection ──────────────────────────────────────────────────────────
export function SheetSection({ section, schema, data, onChange, readOnly, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  const sectionFields = (section.fields || []).flatMap(fieldRef => {
    const base = fieldRef.split('.')[0];
    if (!schema?.properties?.[base]) return [];
    return [{ name: base, schema: schema.properties[base] }];
  });

  if (sectionFields.length === 0) return null;

  return (
    <Card className="glass-card">
      <button
        type="button"
        className="w-full flex items-center justify-between p-4 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <CardTitle className="text-base font-cinzel">{section.title}</CardTitle>
        {open
          ? <ChevronUp   className="h-4 w-4 text-muted-foreground" />
          : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <CardContent className="pt-0 pb-4 px-4 space-y-4">
          {sectionFields.map(({ name, schema: fSchema }) => (
            <FieldRenderer
              key={name}
              name={name}
              schema={fSchema}
              value={data?.[name]}
              onChange={onChange}
              readOnly={readOnly}
            />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

/**
 * Calcule le % de complétude d'une fiche à partir du schema et des données.
 * Utilise les champs "required" du fields_schema.
 */
export function computeCompleteness(fieldsSchema, data) {
  if (!fieldsSchema?.properties) return null;
  const required = fieldsSchema.required || [];
  if (required.length === 0) return null;

  const filled = required.filter(key => {
    const val = data?.[key];
    if (val === null || val === undefined || val === '') return false;
    if (Array.isArray(val) && val.length === 0) return false;
    if (typeof val === 'object' && !Array.isArray(val)) {
      // Objet : considéré rempli si au moins une propriété non-vide
      return Object.values(val).some(v => v !== null && v !== undefined && v !== '');
    }
    return true;
  });

  return Math.round((filled.length / required.length) * 100);
}
